/**
 * HOF-216 — the visible ``Verknüpfung lösen ✕`` control that paints on a
 * selected matched/near-match line edge.
 *
 * #1344 fixed the selection FEEDBACK (3 px stroke, 24 px hit target) and left
 * the TRIGGER keyboard-only; its own file comment records operators who
 * "clicked, saw no change, and never trusted Backspace/Delete to unlink". A
 * better highlight was not the fix — on a trackpad or touch device without a
 * keyboard the path does not exist at all.
 *
 * Presentational and free of ReactFlow: ``MatchedLineEdge`` positions it
 * inside an ``EdgeLabelRenderer``, which is a portal into a DOM node that
 * exists only under a mounted ``<ReactFlow>``. Keeping the button itself
 * outside that is what lets its label, tooltip and event containment be
 * asserted in jsdom.
 */
import React from 'react';

import { UNLINK_ACTION_LABEL, UNLINK_SHORTCUT_HINT } from '../unlinkConfirmation';
import './UnlinkEdgeControl.css';

export interface UnlinkEdgeControlProps {
  onActivate: () => void;
  /**
   * Tooltip + accessible name. Defaults to the line-level copy, which NAMES
   * the Backspace/Delete shortcut.
   *
   * HOF-219 overrides it for the document-level edge, where that shortcut
   * deliberately does nothing (AC 14 keeps ``isManualLineEdge``'s scoping
   * unchanged) — an aria-label promising a keystroke that is inert is worse
   * than no hint at all, and a screen-reader user has no other way to find out.
   */
  hint?: string;
}

export function UnlinkEdgeControl({ onActivate, hint }: UnlinkEdgeControlProps) {
  // Every pointer event is contained. The control sits ON the canvas, so a
  // leaked ``mousedown`` starts a ReactFlow pan and a leaked ``click`` reaches
  // the pane handler — which clears the very selection this control is
  // anchored to, so the button would vanish under the cursor mid-click.
  const contain = (event: React.SyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <button
      type="button"
      // ``nodrag``/``nopan`` are ReactFlow's own opt-out classes; the handlers
      // above are the belt to that braces, since the pane click handler is ours.
      className="nodrag nopan gc-unlink-edge-control"
      title={hint ?? UNLINK_SHORTCUT_HINT}
      aria-label={hint ?? UNLINK_SHORTCUT_HINT}
      onMouseDown={contain}
      onPointerDown={contain}
      onClick={(event) => {
        contain(event);
        onActivate();
      }}
    >
      <span className="gc-unlink-edge-control__label">{UNLINK_ACTION_LABEL}</span>
      <span className="gc-unlink-edge-control__icon" aria-hidden="true">
        ✕
      </span>
    </button>
  );
}

export default UnlinkEdgeControl;
